import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ChevronLeft, Phone, Mail, MapPin, Calendar, Crown, Flag, Users, Package, Plus, Trash2, Edit2, UserPlus, Clock, LogIn, Send, RefreshCw, Copy, Link2, ExternalLink } from 'lucide-react'
import clsx from 'clsx'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { Card, Badge, Button, Modal, ModalHeader, ModalBody, ModalFooter, SlidePanel, Input, Select, Textarea, PaginationBar } from '../../../components/ui'
import { toast } from '../../../components/Toast'
import { formatPhone, maskCPF, maskCEP, unmaskCEP, unmaskCPF, unmaskPhone, maskNumber, unmaskNumber } from '../../../utils/masks'
import { formatDate, formatDateTime } from '../../../utils/dateUtils'
import InviteUserModal from '../../../components/InviteUserModal'
import { inputClass } from '../../../styles/forms'

interface LeaderData {
  id: number; name: string; nickname: string | null; cpf: string | null
  phone: string | null; email: string | null; birth_date: string | null; gender: string | null
  zip_code: string | null; street: string | null; number: string | null
  neighborhood: string | null; complement: string | null
  city_name: string | null; state_code: string | null; neighborhood_name: string | null
  status_code: string; status_label: string
  notes: string | null; created_at: string
}

interface AreaItem { id: number; city_name: string; state_code: string }

interface CampaignItem {
  id: number; campaign_name: string; election_year: number; is_active_campaign: boolean
  vote_goal: number | null; votes_obtained: number | null; is_active: boolean
  campaign_status_label: string
}

interface MaterialItem {
  id: number; material_name: string; unit: string; quantity: number
  delivered_at: string; notes: string | null; created_by_name: string | null
}

interface MaterialType { id: number; name: string; unit: string; is_active: boolean }

interface VoterStats { total: number; voters: number; contacts: number }

interface LinkedUser {
  id: number; name: string; email: string
  last_login_at: string | null; created_at: string
}

interface PendingInvite {
  id: number; name: string; email: string
  token: string; expires_at: string; created_at: string
}

export default function LeaderDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [leader, setLeader] = useState<LeaderData | null>(null)
  const [areas, setAreas] = useState<AreaItem[]>([])
  const [campaigns, setCampaigns] = useState<CampaignItem[]>([])
  const [materials, setMaterials] = useState<MaterialItem[]>([])
  const [voterStats, setVoterStats] = useState<VoterStats>({ total: 0, voters: 0, contacts: 0 })
  // Aba de eleitores cadastrados por esta liderança
  const [leaderVoters, setLeaderVoters] = useState<any[]>([])
  const [votersLoading, setVotersLoading] = useState(false)
  const [votersType, setVotersType] = useState<'all' | 'voters' | 'contacts'>('all')
  const [votersPage, setVotersPage] = useState(1)
  const [votersPerPage, setVotersPerPage] = useState(10)
  const [votersMeta, setVotersMeta] = useState({ total: 0, page: 1, per_page: 10, last_page: 1 })
  const [materialTypes, setMaterialTypes] = useState<MaterialType[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('dados')
  const [materialModalOpen, setMaterialModalOpen] = useState(false)
  const [typeModalOpen, setTypeModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [materialForm, setMaterialForm] = useState({ material_type_id: '', quantity: '', delivered_at: new Date().toISOString().split('T')[0], notes: '' })
  const [typeForm, setTypeForm] = useState({ name: '', unit: 'un' })
  const [linkedUser, setLinkedUser] = useState<LinkedUser | null>(null)
  const [pendingInvite, setPendingInvite] = useState<PendingInvite | null>(null)
  const [publicLink, setPublicLink] = useState<{ slug: string; is_active: boolean; submissions_count: number; last_submission_at: string | null } | null>(null)
  const [linkLoading, setLinkLoading] = useState(false)
  const [resending, setResending] = useState(false)
  const [inviteModalOpen, setInviteModalOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [activeCampaign, setActiveCampaign] = useState<{ id: number; name: string } | null>(null)
  const [editForm, setEditForm] = useState({
    name: '', nickname: '', phone: '', email: '', cpf: '',
    birth_date: '', gender: '',
    zip_code: '', street: '', number: '', neighborhood: '', complement: '',
    notes: '', can_register_voters: false,
    add_to_campaign: false, campaign_vote_goal: '',
  })

  useEffect(() => { if (id) fetchLeader() }, [id])

  useEffect(() => {
    axios.get(buildApiUrl('/v1/campaigns'))
      .then(res => {
        const active = res.data.data?.find((c: any) => c.is_active_campaign)
        setActiveCampaign(active ? { id: active.id, name: active.name } : null)
      })
      .catch(() => {})
  }, [])

  // Buscar eleitores/contatos cadastrados por esta liderança (só quando a aba está ativa)
  useEffect(() => {
    if (activeTab !== 'eleitores' || !id) return
    setVotersLoading(true)
    const params: any = { leader_id: id, page: votersPage, per_page: votersPerPage }
    if (votersType === 'voters') params.is_voter = 1
    if (votersType === 'contacts') params.is_contact = 1
    axios.get(buildApiUrl('/v1/voters'), { params })
      .then(res => { setLeaderVoters(res.data.data); if (res.data.meta) setVotersMeta(res.data.meta) })
      .catch(() => setLeaderVoters([]))
      .finally(() => setVotersLoading(false))
  }, [activeTab, id, votersType, votersPage, votersPerPage])

  async function fetchLeader() {
    setLoading(true)
    try {
      const [leaderRes, typesRes] = await Promise.all([
        axios.get(buildApiUrl(`/v1/leaders/${id}`)),
        axios.get(buildApiUrl('/v1/materials/types')),
      ])
      setLeader(leaderRes.data.data.leader)
      setAreas(leaderRes.data.data.areas || [])
      setCampaigns(leaderRes.data.data.campaigns || [])
      setMaterials(leaderRes.data.data.materials || [])
      setVoterStats(leaderRes.data.data.voter_stats || { total: 0, voters: 0, contacts: 0 })
      setLinkedUser(leaderRes.data.data.linked_user || null)
      setPendingInvite(leaderRes.data.data.pending_invite || null)
      setMaterialTypes(typesRes.data.data)
      axios.get(buildApiUrl(`/v1/leaders/${id}/public-link`))
        .then(r => setPublicLink(r.data.data))
        .catch(() => setPublicLink(null))
    } catch { /* */ } finally { setLoading(false) }
  }

  // URL pública da rede (rede.sispoli.com.br em prd, rede.sispoli.local em dev)
  function redeBaseUrl() {
    const h = window.location.hostname
    if (h.endsWith('.sispoli.local')) return 'http://rede.sispoli.local'
    if (h.endsWith('.sispoli.com.br')) return 'https://rede.sispoli.com.br'
    return `${window.location.protocol}//rede.${h.replace(/^app\./, '')}`
  }
  const publicLinkUrl = publicLink ? `${redeBaseUrl()}/${publicLink.slug}` : ''

  async function createPublicLink() {
    if (linkLoading) return
    setLinkLoading(true)
    try {
      const res = await axios.post(buildApiUrl(`/v1/leaders/${id}/public-link`))
      setPublicLink(res.data.data)
      toast('success', 'Link de captação gerado')
    } catch { toast('error', 'Erro ao gerar link') } finally { setLinkLoading(false) }
  }

  async function togglePublicLink() {
    if (linkLoading) return
    setLinkLoading(true)
    try {
      const res = await axios.put(buildApiUrl(`/v1/leaders/${id}/public-link/toggle`))
      setPublicLink(res.data.data)
      toast('success', res.data.data.is_active ? 'Link ativado' : 'Link desativado')
    } catch { toast('error', 'Erro ao alterar link') } finally { setLinkLoading(false) }
  }

  async function saveMaterial() {
    if (!materialForm.material_type_id || !materialForm.quantity || saving) return
    setSaving(true)
    try {
      await axios.post(buildApiUrl('/v1/materials/distributions'), {
        leader_id: Number(id),
        material_type_id: Number(materialForm.material_type_id),
        quantity: Number(materialForm.quantity),
        delivered_at: materialForm.delivered_at,
        notes: materialForm.notes || null,
      })
      toast('success', 'Material registrado')
      setMaterialModalOpen(false)
      setMaterialForm({ material_type_id: '', quantity: '', delivered_at: new Date().toISOString().split('T')[0], notes: '' })
      fetchLeader()
    } catch { toast('error', 'Erro ao registrar') } finally { setSaving(false) }
  }

  async function deleteMaterial(matId: number) {
    await axios.delete(buildApiUrl(`/v1/materials/distributions/${matId}`))
    toast('success', 'Registro removido')
    fetchLeader()
  }

  async function saveType() {
    if (!typeForm.name || saving) return
    setSaving(true)
    try {
      await axios.post(buildApiUrl('/v1/materials/types'), typeForm)
      toast('success', 'Tipo criado')
      setTypeModalOpen(false)
      setTypeForm({ name: '', unit: 'un' })
      const res = await axios.get(buildApiUrl('/v1/materials/types'))
      setMaterialTypes(res.data.data)
    } catch { toast('error', 'Erro ao criar tipo') } finally { setSaving(false) }
  }

  async function resendInvite() {
    if (!pendingInvite || resending) return
    setResending(true)
    try {
      await axios.post(buildApiUrl(`/v1/organization-users/invites/${pendingInvite.id}/resend`))
      toast('success', 'Convite reenviado')
      fetchLeader()
    } catch { toast('error', 'Erro ao reenviar') } finally { setResending(false) }
  }

  function openEdit() {
    if (!leader) return
    const activeLink = campaigns.find(c => c.is_active_campaign && c.is_active)
    setEditForm({
      name: leader.name || '', nickname: leader.nickname || '',
      phone: leader.phone || '', email: leader.email || '', cpf: leader.cpf || '',
      birth_date: leader.birth_date || '', gender: leader.gender || '',
      zip_code: leader.zip_code || '', street: leader.street || '',
      number: leader.number || '', neighborhood: leader.neighborhood || '',
      complement: leader.complement || '', notes: leader.notes || '',
      can_register_voters: !!(leader as any).can_register_voters,
      add_to_campaign: !!activeLink,
      campaign_vote_goal: activeLink?.vote_goal ? maskNumber(String(activeLink.vote_goal)) : '',
    })
    setEditOpen(true)
  }

  async function handleEditSave() {
    if (saving) return
    setSaving(true)
    try {
      const { add_to_campaign, campaign_vote_goal, ...leaderFields } = editForm
      await axios.put(buildApiUrl(`/v1/leaders/${id}`), {
        ...leaderFields,
        phone: unmaskPhone(editForm.phone) || editForm.phone,
        cpf: unmaskCPF(editForm.cpf) || editForm.cpf,
        zip_code: unmaskCEP(editForm.zip_code) || editForm.zip_code,
      })

      // Engajar na campanha ativa (ou atualizar a meta)
      if (activeCampaign && add_to_campaign) {
        await axios.post(buildApiUrl(`/v1/leaders/${id}/campaign`), {
          campaign_id: activeCampaign.id,
          vote_goal: unmaskNumber(campaign_vote_goal),
        })
      }

      toast('success', 'Dados atualizados')
      setEditOpen(false)
      fetchLeader()
    } catch { toast('error', 'Erro ao salvar') } finally { setSaving(false) }
  }

  if (loading) return (
    <div className="space-y-6 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg bg-gray-200" />
        <div className="h-7 w-48 bg-gray-200 rounded" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[1,2,3,4].map(i => <div key={i} className="h-20 bg-gray-100 rounded-2xl" />)}
      </div>
      <Card><div className="h-40 bg-gray-50 rounded-xl" /></Card>
    </div>
  )

  if (!leader) return <div className="text-center py-20 text-gray-500">Lideranca nao encontrada</div>

  const address = [leader.street, leader.number].filter(Boolean).join(', ')
  const neighborhood = leader.neighborhood_name || leader.neighborhood
  const cityState = leader.city_name ? `${leader.city_name}/${leader.state_code}` : null

  // Agrupar materiais por tipo
  const materialSummary = materials.reduce<Record<string, { name: string; unit: string; total: number }>>((acc, m) => {
    if (!acc[m.material_name]) acc[m.material_name] = { name: m.material_name, unit: m.unit, total: 0 }
    acc[m.material_name].total += m.quantity
    return acc
  }, {})

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/leaders')} className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-gray-200/60 text-gray-400 hover:text-gray-600 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{leader.name}</h1>
            {leader.nickname && <p className="text-gray-500 text-sm">{leader.nickname}</p>}
          </div>
          <Badge variant={leader.status_code === 'ACTIVE' ? 'success' : 'gray'} dot size="sm">
            {leader.status_label}
          </Badge>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { icon: <Users className="w-5 h-5 text-brand-500" />, value: voterStats.total, label: 'Pessoas' },
          { icon: <Crown className="w-5 h-5 text-emerald-500" />, value: voterStats.voters, label: 'Eleitores' },
          { icon: <MapPin className="w-5 h-5 text-blue-500" />, value: areas.length, label: 'Areas' },
          { icon: <Package className="w-5 h-5 text-amber-500" />, value: materials.length, label: 'Entregas' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-2xl border border-gray-200 p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-xl bg-gray-50 flex items-center justify-center">{s.icon}</div>
            </div>
            <p className="text-2xl font-semibold text-gray-900">{s.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-6 border-b border-gray-200">
        {[
          { id: 'dados', label: 'Dados' },
          { id: 'eleitores', label: `Eleitores${voterStats.total ? ` (${voterStats.total})` : ''}` },
          { id: 'materiais', label: `Materiais${materials.length ? ` (${materials.length})` : ''}` },
          { id: 'campanhas', label: `Campanhas${campaigns.length ? ` (${campaigns.length})` : ''}` },
          { id: 'acesso', label: 'Acesso' },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={clsx('pb-2.5 text-sm font-medium transition-colors border-b-2 -mb-px', activeTab === tab.id ? 'text-brand-600 border-brand-500' : 'text-gray-500 border-transparent hover:text-gray-700')}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab: Eleitores */}
      {activeTab === 'eleitores' && (
        <Card padding="none">
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              {([['all', 'Todos'], ['voters', 'Eleitores'], ['contacts', 'Contatos']] as const).map(([v, l]) => (
                <button key={v} onClick={() => { setVotersType(v); setVotersPage(1) }}
                  className={clsx('px-3 py-1.5 rounded-md text-sm font-medium transition-colors', votersType === v ? 'bg-white text-brand-600 shadow-xs' : 'text-gray-500 hover:text-gray-700')}>
                  {l}
                </button>
              ))}
            </div>
            <span className="text-sm text-gray-500">{votersMeta.total} {votersMeta.total === 1 ? 'pessoa' : 'pessoas'}</span>
          </div>

          {votersLoading ? (
            <div className="p-10 text-center text-sm text-gray-400">Carregando…</div>
          ) : leaderVoters.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-700">Nenhum registro cadastrado por esta liderança</p>
              <p className="text-xs text-gray-400 mt-1">Eleitores e contatos vinculados a esta liderança aparecem aqui.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telefone</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Cidade</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Canal</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {leaderVoters.map(v => (
                      <tr key={v.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => navigate(`/voters/${v.id}`)}>
                        <td className="px-4 py-3.5">
                          <p className="text-sm font-medium text-gray-900">{v.name}</p>
                          {v.nickname && <p className="text-xs text-gray-400">{v.nickname}</p>}
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-700">{v.phone ? formatPhone(v.phone) : '—'}</td>
                        <td className="px-4 py-3.5 text-sm text-gray-700 hidden sm:table-cell">{v.city_name ? `${v.city_name}/${v.state_code}` : '—'}</td>
                        <td className="px-4 py-3.5 text-sm text-gray-500 hidden sm:table-cell">{v.channel_label || '—'}</td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-1.5">
                            {v.is_voter ? <Badge variant="success" size="sm">Eleitor</Badge> : null}
                            {v.is_contact ? <Badge variant="gray" size="sm">Contato</Badge> : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <PaginationBar page={votersPage} perPage={votersPerPage} total={votersMeta.total} lastPage={votersMeta.last_page} onPageChange={setVotersPage} onPerPageChange={n => { setVotersPerPage(n); setVotersPage(1) }} />
            </>
          )}
        </Card>
      )}

      {/* Tab: Dados */}
      {activeTab === 'dados' && (<>
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Informações</h3>
            <Button variant="outline" size="sm" onClick={openEdit}>
              <Edit2 className="w-3.5 h-3.5 mr-1.5" />Editar
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-4 pb-6 border-b border-gray-100">
            <Field label="Nome" value={leader.name} />
            <Field label="Apelido" value={leader.nickname || '—'} muted={!leader.nickname} />
            {leader.phone && (
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Telefone</p>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-900">{formatPhone(leader.phone)}</span>
                  <button onClick={() => { navigator.clipboard?.writeText(leader.phone || ''); toast('success', 'Número copiado') }} className="text-gray-400 hover:text-brand-500 transition-colors" title="Copiar número">
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                  <a href={`https://wa.me/55${leader.phone}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-green-500 transition-colors" title="WhatsApp">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  </a>
                </div>
              </div>
            )}
            {leader.email && <Field label="Email" value={leader.email} />}
            {leader.cpf && <Field label="CPF" value={maskCPF(leader.cpf)} />}
            {leader.birth_date && <Field label="Nascimento" value={formatDate(leader.birth_date)} />}
            {leader.gender && <Field label="Gênero" value={leader.gender === 'M' ? 'Masculino' : leader.gender === 'F' ? 'Feminino' : 'Outro'} />}
          </div>

          {/* Endereço */}
          <div className="py-6 border-b border-gray-100">
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Endereço</h4>
            {address || neighborhood || cityState ? (
              <div>
                <p className="text-sm text-gray-900">{[address, leader.complement, leader.zip_code && `CEP ${leader.zip_code}`].filter(Boolean).join(' - ')}</p>
                {neighborhood && <p className="text-sm text-gray-700 mt-0.5">{neighborhood}</p>}
                {cityState && <p className="text-sm text-gray-500 mt-0.5">{cityState}</p>}
              </div>
            ) : <p className="text-sm text-gray-300">—</p>}
          </div>

          {/* Observações + Apto */}
          <div className="pt-6 grid grid-cols-2 gap-6">
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Observações</h4>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{leader.notes || '—'}</p>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Apto a cadastrar</h4>
              <Badge variant={(leader as any).can_register_voters ? 'success' : 'gray'} size="sm" dot>
                {(leader as any).can_register_voters ? 'Sim' : 'Não'}
              </Badge>
            </div>
          </div>
        </Card>

        {/* Areas de atuacao */}
        {areas.length > 0 && (
          <Card>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Areas de atuacao</h3>
            <div className="flex flex-wrap gap-2">
              {areas.map(a => (
                <span key={a.id} className="text-sm px-3 py-1.5 bg-brand-50 text-brand-600 rounded-lg font-medium">
                  {a.city_name}/{a.state_code}
                </span>
              ))}
            </div>
          </Card>
        )}

        {/* Registro */}
        <Card>
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Registro</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-3">
            <Field label="Cadastrado por" value={(leader as any).created_by_name || '—'} muted={!(leader as any).created_by_name} />
            <Field label="Data do cadastro" value={formatDateTime(leader.created_at)} />
            <Field label="Última edição" value={(leader as any).updated_at ? formatDateTime((leader as any).updated_at) : '—'} muted={!(leader as any).updated_at} />
          </div>
        </Card>
      </>)}

      {/* Tab: Materiais */}
      {activeTab === 'materiais' && (<>
        {/* Resumo por tipo */}
        {Object.keys(materialSummary).length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Object.values(materialSummary).map(m => (
              <div key={m.name} className="bg-white rounded-xl border border-gray-200 p-3 text-center">
                <p className="text-lg font-semibold text-gray-900">{m.total.toLocaleString('pt-BR')}</p>
                <p className="text-xs text-gray-500">{m.name} ({m.unit})</p>
              </div>
            ))}
          </div>
        )}

        <Card padding="none">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-800">Histórico de entregas</h3>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setTypeModalOpen(true)}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Tipo
              </Button>
              <Button size="sm" onClick={() => setMaterialModalOpen(true)}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Registrar entrega
              </Button>
            </div>
          </div>

          {materials.length === 0 ? (
            <div className="py-12 text-center">
              <Package className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-400">Nenhum material distribuido</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Material</th>
                    <th className="text-right px-3 py-3 text-xs font-semibold text-gray-500 uppercase">Qtd</th>
                    <th className="text-left px-3 py-3 text-xs font-semibold text-gray-500 uppercase">Data</th>
                    <th className="text-left px-3 py-3 text-xs font-semibold text-gray-500 uppercase">Obs</th>
                    <th className="text-left px-3 py-3 text-xs font-semibold text-gray-500 uppercase">Por</th>
                    <th className="px-3 py-3 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {materials.map(m => (
                    <tr key={m.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                      <td className="px-5 py-3">
                        <span className="text-sm font-medium text-gray-900">{m.material_name}</span>
                        <span className="text-xs text-gray-400 ml-1">({m.unit})</span>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <span className="text-sm font-semibold text-gray-900">{m.quantity.toLocaleString('pt-BR')}</span>
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-600">{formatDate(m.delivered_at)}</td>
                      <td className="px-3 py-3 text-sm text-gray-500 max-w-[200px] truncate">{m.notes || '—'}</td>
                      <td className="px-3 py-3 text-xs text-gray-400">{m.created_by_name || '—'}</td>
                      <td className="px-3 py-3">
                        <button onClick={() => deleteMaterial(m.id)} className="h-7 w-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-error-500 hover:bg-error-50 transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </>)}

      {/* Tab: Campanhas */}
      {activeTab === 'campanhas' && (
        <Card>
          {campaigns.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-12">Nao participou de nenhuma campanha</p>
          ) : (
            <div className="space-y-3">
              {campaigns.map(c => {
                const goalPct = c.vote_goal && c.vote_goal > 0 ? Math.round(((c.votes_obtained || 0) / c.vote_goal) * 100) : null
                return (
                  <div key={c.id} className="flex items-center justify-between p-4 rounded-xl border border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center', c.is_active_campaign ? 'bg-brand-50 text-brand-500' : 'bg-gray-100 text-gray-400')}>
                        <Flag className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-800">{c.campaign_name}</span>
                          {c.is_active_campaign && <span className="text-[10px] font-bold text-brand-500 bg-brand-50 px-1.5 py-0.5 rounded">ATIVA</span>}
                        </div>
                        <div className="flex items-center gap-3 mt-0.5">
                          <span className="text-xs text-gray-500">{c.election_year}</span>
                          {c.vote_goal != null && (
                            <span className="text-xs text-gray-500">Meta: {c.vote_goal.toLocaleString('pt-BR')} votos</span>
                          )}
                          {goalPct != null && (
                            <span className={clsx('text-xs font-medium', goalPct >= 100 ? 'text-success-600' : goalPct >= 50 ? 'text-warning-600' : 'text-error-500')}>
                              {goalPct}%
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <Badge variant={c.is_active ? 'success' : 'gray'} size="sm" dot>
                      {c.is_active ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </div>
                )
              })}
            </div>
          )}
        </Card>
      )}

      {/* Tab: Acesso */}
      {activeTab === 'acesso' && (
        <div className="space-y-6">
        {linkedUser ? (
          <Card>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Usuário vinculado</h3>
              <Badge variant="success" size="sm" dot>Ativo</Badge>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-4 mb-6">
              <Field label="Nome" value={linkedUser.name} />
              <Field label="Email" value={linkedUser.email} />
              <Field label="Conta criada em" value={formatDateTime(linkedUser.created_at)} />
            </div>

            <div className="border-t border-gray-100 pt-4">
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Acessos</h4>
              <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                <div className="flex items-center gap-2">
                  <LogIn className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-400">Último login</p>
                    <p className="text-sm text-gray-800">{linkedUser.last_login_at ? formatDateTime(linkedUser.last_login_at) : 'Nunca acessou'}</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        ) : pendingInvite ? (
          <Card>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-11 h-11 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0">
                <Send className="w-5 h-5 text-amber-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <p className="text-sm font-medium text-gray-900">Convite enviado</p>
                  {new Date(pendingInvite.expires_at) < new Date()
                    ? <Badge variant="gray" size="sm">Expirado</Badge>
                    : <Badge variant="warning" size="sm" dot>Aguardando aceite</Badge>
                  }
                </div>
                <p className="text-xs text-gray-500">Enviado para <strong className="text-gray-700">{pendingInvite.email}</strong></p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-3 pb-5 border-b border-gray-100">
              <Field label="Nome" value={pendingInvite.name} />
              <Field label="E-mail" value={pendingInvite.email} />
              <Field label="Enviado em" value={formatDateTime(pendingInvite.created_at)} />
              <Field label="Expira em" value={formatDateTime(pendingInvite.expires_at)} muted={new Date(pendingInvite.expires_at) < new Date()} />
            </div>

            <div className="pt-4 flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={resendInvite} loading={resending}>
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Reenviar convite
              </Button>
              {new Date(pendingInvite.expires_at) > new Date() && (
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/register?token=${pendingInvite.token}`); toast('success', 'Link copiado') }}>
                  <Copy className="w-3.5 h-3.5 mr-1.5" />Copiar convite
                </Button>
              )}
            </div>
          </Card>
        ) : (
          <Card>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-11 h-11 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                <UserPlus className="w-5 h-5 text-gray-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Sem acesso à plataforma</p>
                <p className="text-xs text-gray-500">Esta liderança ainda não possui usuário vinculado</p>
              </div>
            </div>
            <p className="text-sm text-gray-500 mb-4">Envie um convite para que esta liderança possa acessar a plataforma, acompanhar seus contatos e contribuir com a gestão.</p>
            <Button size="sm" onClick={() => setInviteModalOpen(true)}>
              <UserPlus className="w-4 h-4 mr-1.5" />Enviar convite
            </Button>
          </Card>
        )}

        {/* Link público de captação (rede da liderança) */}
        <Card>
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-brand-50 flex items-center justify-center flex-shrink-0">
                <Link2 className="w-5 h-5 text-brand-500" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Link público de captação</p>
                <p className="text-xs text-gray-500">Página pública para esta liderança cadastrar eleitores da sua rede</p>
              </div>
            </div>
            {publicLink && (
              <Badge variant={publicLink.is_active ? 'success' : 'gray'} size="sm" dot>
                {publicLink.is_active ? 'Ativo' : 'Desativado'}
              </Badge>
            )}
          </div>

          {!publicLink ? (
            <div className="pt-1">
              <p className="text-sm text-gray-500 mb-4">Gere um link exclusivo. Qualquer pessoa com o link poderá cadastrar eleitores, que entram automaticamente vinculados a esta liderança.</p>
              <Button size="sm" onClick={createPublicLink} loading={linkLoading}>
                <Link2 className="w-4 h-4 mr-1.5" />Gerar link de captação
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={publicLinkUrl}
                  className="flex-1 h-11 px-3 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-700 font-mono"
                  onFocus={e => e.currentTarget.select()}
                />
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(publicLinkUrl); toast('success', 'Link copiado') }}>
                  <Copy className="w-4 h-4 mr-1.5" />Copiar
                </Button>
                <Button size="sm" variant="outline" onClick={() => window.open(publicLinkUrl, '_blank')}>
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </div>

              <div className="flex items-center justify-between border-t border-gray-100 pt-4">
                <div>
                  <p className="text-xs text-gray-400">Cadastros recebidos</p>
                  <p className="text-lg font-semibold text-gray-900">{publicLink.submissions_count}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600">{publicLink.is_active ? 'Link disponível' : 'Link indisponível'}</span>
                  <button
                    onClick={togglePublicLink}
                    disabled={linkLoading}
                    className={clsx('w-11 h-6 rounded-full transition-colors relative disabled:opacity-50', publicLink.is_active ? 'bg-brand-500' : 'bg-gray-300')}
                    title={publicLink.is_active ? 'Desativar link' : 'Ativar link'}
                  >
                    <span className={clsx('absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-xs transition-transform', publicLink.is_active ? 'left-[22px]' : 'left-0.5')} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </Card>
        </div>
      )}

      {/* Modal convidar usuário (perfil fixo como Lideranca) */}
      <InviteUserModal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        onSuccess={fetchLeader}
        defaultName={leader?.name}
        defaultEmail={leader?.email || undefined}
        fixedProfileId={4}
      />

      {/* Modal registrar entrega */}
      <Modal open={materialModalOpen} onClose={() => setMaterialModalOpen(false)} size="sm">
        <ModalHeader onClose={() => setMaterialModalOpen(false)}>Registrar entrega</ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Material</label>
              <select value={materialForm.material_type_id} onChange={e => setMaterialForm(f => ({ ...f, material_type_id: e.target.value }))} className={clsx(inputClass, 'appearance-none')}>
                <option value="">Selecione...</option>
                {materialTypes.filter(t => t.is_active).map(t => (
                  <option key={t.id} value={t.id}>{t.name} ({t.unit})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Quantidade</label>
              <input type="number" min="1" value={materialForm.quantity} onChange={e => setMaterialForm(f => ({ ...f, quantity: e.target.value }))} placeholder="Ex: 500" className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Data da entrega</label>
              <input type="date" value={materialForm.delivered_at} onChange={e => setMaterialForm(f => ({ ...f, delivered_at: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Observacao</label>
              <input value={materialForm.notes} onChange={e => setMaterialForm(f => ({ ...f, notes: e.target.value }))} placeholder="Opcional" className={inputClass} />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setMaterialModalOpen(false)}>Cancelar</Button>
          <Button onClick={saveMaterial} disabled={!materialForm.material_type_id || !materialForm.quantity} loading={saving}>Registrar</Button>
        </ModalFooter>
      </Modal>

      {/* Modal novo tipo de material */}
      <Modal open={typeModalOpen} onClose={() => setTypeModalOpen(false)} size="sm">
        <ModalHeader onClose={() => setTypeModalOpen(false)}>Novo tipo de material</ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Nome</label>
              <input value={typeForm.name} onChange={e => setTypeForm(f => ({ ...f, name: e.target.value }))} placeholder="Ex: Santinho, Adesivo, Bandeira..." className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Unidade</label>
              <select value={typeForm.unit} onChange={e => setTypeForm(f => ({ ...f, unit: e.target.value }))} className={clsx(inputClass, 'appearance-none')}>
                <option value="un">Unidade (un)</option>
                <option value="cx">Caixa (cx)</option>
                <option value="pct">Pacote (pct)</option>
                <option value="kg">Quilo (kg)</option>
                <option value="m">Metro (m)</option>
              </select>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setTypeModalOpen(false)}>Cancelar</Button>
          <Button onClick={saveType} disabled={!typeForm.name} loading={saving}>Criar tipo</Button>
        </ModalFooter>
      </Modal>

      {/* SlidePanel editar dados */}
      <SlidePanel open={editOpen} onClose={() => setEditOpen(false)} title="Editar liderança" size="lg">
        <div className="p-6 space-y-4">
          <Input label="Nome" value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
          <Input label="Apelido" value={editForm.nickname} onChange={e => setEditForm(f => ({ ...f, nickname: e.target.value }))} />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Telefone" value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} />
            <Input label="Email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="CPF" value={editForm.cpf} onChange={e => setEditForm(f => ({ ...f, cpf: e.target.value }))} />
            <Input label="Data de nascimento" type="date" value={editForm.birth_date} onChange={e => setEditForm(f => ({ ...f, birth_date: e.target.value }))} />
          </div>
          <Select label="Gênero" value={editForm.gender} onChange={e => setEditForm(f => ({ ...f, gender: e.target.value }))} options={[{ value: 'M', label: 'Masculino' }, { value: 'F', label: 'Feminino' }, { value: 'O', label: 'Outro' }]} placeholder="Selecione" />

          <div className="border-t border-gray-100 pt-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Endereço</p>
            <div className="space-y-4">
              <Input label="CEP" value={editForm.zip_code} onChange={e => setEditForm(f => ({ ...f, zip_code: e.target.value }))} />
              <Input label="Rua" value={editForm.street} onChange={e => setEditForm(f => ({ ...f, street: e.target.value }))} />
              <div className="grid grid-cols-2 gap-4">
                <Input label="Número" value={editForm.number} onChange={e => setEditForm(f => ({ ...f, number: e.target.value }))} />
                <Input label="Bairro" value={editForm.neighborhood} onChange={e => setEditForm(f => ({ ...f, neighborhood: e.target.value }))} />
              </div>
              <Input label="Complemento" value={editForm.complement} onChange={e => setEditForm(f => ({ ...f, complement: e.target.value }))} />
            </div>
          </div>

          <Textarea label="Observações" value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} rows={3} />

          {/* Apto a cadastrar */}
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => setEditForm(f => ({ ...f, can_register_voters: !f.can_register_voters }))}>
            <div className={clsx('w-10 h-6 rounded-full transition-colors relative', editForm.can_register_voters ? 'bg-brand-500' : 'bg-gray-300')}>
              <span className={clsx('absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-xs transition-transform', editForm.can_register_voters ? 'left-[18px]' : 'left-0.5')} />
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700">Apto a cadastrar contatos</span>
              <p className="text-xs text-gray-400">Indica que esta liderança pode registrar novos contatos</p>
            </div>
          </div>

          {/* Engajar na campanha ativa */}
          {activeCampaign && (
            <div className="border-t border-gray-100 pt-4">
              <div className="flex items-center gap-3 p-4 rounded-xl bg-brand-25 border border-brand-100 mb-3 cursor-pointer" onClick={() => setEditForm(f => ({ ...f, add_to_campaign: !f.add_to_campaign }))}>
                <input type="checkbox" checked={editForm.add_to_campaign} onChange={() => {}}
                  className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Flag className="w-4 h-4 text-brand-500" />
                    <span className="text-sm font-medium text-gray-800">Engajar na campanha ativa</span>
                  </div>
                  <p className="text-xs text-brand-500 mt-0.5">{activeCampaign.name}</p>
                </div>
              </div>

              {editForm.add_to_campaign && (
                <Input
                  label="Meta de votos para esta liderança"
                  inputMode="numeric"
                  value={editForm.campaign_vote_goal}
                  onChange={e => setEditForm(f => ({ ...f, campaign_vote_goal: maskNumber(e.target.value) }))}
                  placeholder="5.000"
                />
              )}
            </div>
          )}

          <div className="pt-4 flex gap-3">
            <Button onClick={handleEditSave} loading={saving} className="flex-1">Salvar</Button>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="flex-1">Cancelar</Button>
          </div>
        </div>
      </SlidePanel>
    </div>
  )
}

function Field({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div>
      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
      <p className={clsx('text-sm', muted ? 'text-gray-300' : 'text-gray-900')}>{value}</p>
    </div>
  )
}
